import { pctLabel, type OrderInput, type OrderSettingsRow, type OrderTotals } from "@/lib/orders";
import { Card, Label, Toggle } from "./ui";

function bracketLabel(brackets: OrderSettingsRow["brackets"], i: number): string {
  const b = brackets[i];
  if (!b) return "";
  const lo = i === 0 ? 0 : (brackets[i - 1]?.maxSqm ?? 0);
  if (b.maxSqm === null) return `${lo.toLocaleString("en-US")} m² and over`;
  if (i === 0) return `under ${b.maxSqm} m²`;
  return `${lo.toLocaleString("en-US")} – ${(b.maxSqm - 1).toLocaleString("en-US")} m²`;
}

/** Quantity bracket, discount, dates, notes — the job-level block. */
export function JobCard({
  draft,
  set,
  totals,
  settings,
  disabled,
  isAdmin,
}: {
  draft: OrderInput;
  set: (patch: Partial<OrderInput>) => void;
  totals: OrderTotals | null;
  settings: OrderSettingsRow;
  disabled: boolean;
  isAdmin: boolean;
}) {
  const auto = draft.bracketPct == null;
  const totalSqm = totals?.customSqm ?? 0;
  const current = auto ? (totals?.bracketPct ?? 100) : draft.bracketPct!;
  const overThreshold = (totals?.discountEffectivePct ?? 0) > settings.approvalDiscountPct;

  return (
    <Card tag="3 · Quantity & discount">
      <div className="space-y-4">
        <div>
          <Label>Quantity bracket · custom rooms m² (packages excluded) · {auto ? "automatic" : "set by hand"}</Label>
          <div className="flex flex-wrap gap-2">
            <Toggle
              tone="green"
              on={auto}
              label="Auto"
              value={auto ? pctLabel(current) : undefined}
              disabled={disabled}
              onClick={() => set({ bracketPct: null })}
            />
            {settings.brackets.map((b, i) => (
              <Toggle
                key={i}
                tone="green"
                on={current === b.pct}
                label={bracketLabel(settings.brackets, i)}
                value={pctLabel(b.pct)}
                disabled={disabled}
                onClick={() => set({ bracketPct: b.pct })}
              />
            ))}
          </div>
          <p className="mt-1.5 text-[0.68rem] text-ice/50">
            {auto
              ? `Chosen from the total m² (${totalSqm} m²). Tap a bracket to force it.`
              : "Forced by hand — tap Auto to follow the m² again."}
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-[auto_8rem_1fr]">
          <div>
            <Label>Discount</Label>
            <div className="flex gap-1 rounded-full border border-white/15 p-0.5">
              {([null, "percent", "fixed"] as const).map((k) => (
                <button
                  key={String(k)}
                  type="button"
                  disabled={disabled}
                  onClick={() => set({ discountType: k, discountValue: k ? draft.discountValue ?? 0 : null })}
                  className={`rounded-full px-3 py-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.14em] ${(draft.discountType ?? null) === k ? "bg-white/15 text-paper" : "text-ice/55"}`}
                >
                  {k === null ? "none" : k === "percent" ? "%" : "AED"}
                </button>
              ))}
            </div>
          </div>
          <label className="block">
            <Label>{draft.discountType === "fixed" ? "Amount AED" : "Percent"}</Label>
            <input
              className="field num"
              type="number"
              inputMode="decimal"
              min={0}
              dir="ltr"
              disabled={disabled || !draft.discountType}
              value={draft.discountValue ?? ""}
              onChange={(e) => set({ discountValue: Number(e.target.value) })}
            />
          </label>
          <label className="block">
            <Label>Reason {overThreshold ? "* required above " + settings.approvalDiscountPct + "%" : "(optional)"}</Label>
            <input className="field" disabled={disabled || !draft.discountType} value={draft.discountReason ?? ""} onChange={(e) => set({ discountReason: e.target.value })} placeholder="launch offer, referral, …" />
          </label>
        </div>
        {overThreshold && (
          <p className="rounded-lg border border-gold/50 bg-gold/10 px-3 py-2 text-xs text-goldbright">
            Discount of {totals?.discountEffectivePct}% is above the {settings.approvalDiscountPct}% threshold —{" "}
            {isAdmin ? "as admin you can still send it (auto-approved)." : "an admin must approve before it can be sent."}
          </p>
        )}

        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block">
            <Label>Site survey</Label>
            <input className="field" type="date" disabled={disabled} value={draft.surveyDate ?? ""} onChange={(e) => set({ surveyDate: e.target.value || null })} />
          </label>
          <label className="block">
            <Label>Installation</Label>
            <input className="field" type="date" disabled={disabled} value={draft.installDate ?? ""} onChange={(e) => set({ installDate: e.target.value || null })} />
          </label>
          <label className="block">
            <Label>Valid until</Label>
            <input className="field" type="date" disabled={disabled} value={draft.validUntil ?? ""} onChange={(e) => set({ validUntil: e.target.value || undefined })} />
          </label>
        </div>
        <label className="block">
          <Label>Order notes</Label>
          <textarea className="field min-h-14" disabled={disabled} value={draft.notes ?? ""} onChange={(e) => set({ notes: e.target.value })} placeholder="Printed on the order the customer signs." />
        </label>
        <label className="mt-3 block">
          <Label>Note for the site team</Label>
          <textarea className="field min-h-14" disabled={disabled} value={draft.teamNotes ?? ""} onChange={(e) => set({ teamNotes: e.target.value })} placeholder="Internal — never printed. Access, parking, existing floor, customer habits… copied onto the project." />
        </label>
      </div>
    </Card>
  );
}
